import React, { useState } from "react";
import { addYears } from "date-fns";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTranslation } from "react-i18next";
import EAScheduleDatePicker from "../../shared/EAScheduleDatePicker/EAScheduleDatePicker";
import EACalendarModal from "../../shared/CalendarModal/EACalendarModal";
import ScheduleBar from "../../shared/ScheduleBar";
import SwimmingFiltersModal from "./SwimmingFiltersModal";
import { SwimmingExerciseGroup, SwimmingFilters, SwimmingInstructor } from "../../../models/SwimmingScheduleData";
import "./style.scss";

interface IProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  filters: SwimmingFilters;
  onFiltersChange: (filters: SwimmingFilters) => void;
  onClearFilters: () => void;
  availableLocations: string[];
  availableInstructors: SwimmingInstructor[];
  availableExerciseGroups: SwimmingExerciseGroup[];
  hideFilters?: boolean;
  minDate?: Date;
  maxDate?: Date;
  isWeekly?: boolean;
  onPreviousPeriod?: () => void;
  onNextPeriod?: () => void;
  canGoPrevious?: boolean;
  canGoNext?: boolean;
  dateRangeLabel?: string;
}

const countActiveFilters = (filters: SwimmingFilters): number => {
  let count = 0;
  if (filters.onlyAvailable) count++;
  if (filters.instructor) count++;
  if (filters.exerciseGroups.length > 0) count++;
  if (filters.courseType !== "ALL") count++;
  if (filters.locations.length > 0) count++;
  return count;
};

const SwimmingBar: React.FC<IProps> = ({
  selectedDate,
  onDateChange,
  filters,
  onFiltersChange,
  onClearFilters,
  availableLocations,
  availableInstructors,
  availableExerciseGroups,
  hideFilters = false,
  minDate,
  maxDate,
  isWeekly = false,
  onPreviousPeriod,
  onNextPeriod,
  canGoPrevious = true,
  canGoNext = true,
  dateRangeLabel,
}) => {
  const { t } = useTranslation();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 980px)");
  const activeCount = countActiveFilters(filters);

  const dayNavigation = !isWeekly ? (
    <EAScheduleDatePicker
      selectedDate={selectedDate}
      onDateChange={onDateChange}
      requireConfirmation={false}
      minDate={minDate}
      maxDate={maxDate}
    />
  ) : undefined;

  return (
    <>
      <ScheduleBar
        hideFilters={hideFilters}
        activeFilterCount={activeCount}
        onOpenFilters={() => setFiltersOpen(true)}
        onClearFilters={onClearFilters}
        dateLabel={dateRangeLabel ?? ""}
        onPrevious={onPreviousPeriod ?? (() => {})}
        onNext={onNextPeriod ?? (() => {})}
        canGoPrevious={canGoPrevious}
        canGoNext={canGoNext}
        onOpenCalendar={() => setCalendarOpen(true)}
        previousTitle={isMobile ? t("poprzedni_dzien", "Poprzedni dzień") : t("poprzedni_tydzien")}
        nextTitle={isMobile ? t("nastepny_dzien", "Następny dzień") : t("nastepny_tydzien")}
        navigation={dayNavigation}
      />

      {filtersOpen && (
        <SwimmingFiltersModal
          isOpen={true}
          onClose={() => setFiltersOpen(false)}
          filters={filters}
          onApply={onFiltersChange}
          availableLocations={availableLocations}
          availableInstructors={availableInstructors}
          availableExerciseGroups={availableExerciseGroups}
          isWeekly={isWeekly}
        />
      )}

      {isWeekly && calendarOpen && (
        <EACalendarModal
          onClose={() => setCalendarOpen(false)}
          onDatePick={(date) => {
            onDateChange(date);
            setCalendarOpen(false);
          }}
          date={selectedDate}
          title="Wybór tygodnia"
          fromDate={minDate ?? new Date()}
          toDate={maxDate ?? addYears(new Date(), 1)}
        />
      )}
    </>
  );
};

export default SwimmingBar;
